import type {
  AdminTicketCategory,
  AdminTicketListItemDto,
  AdminTicketStatus,
  AdminTicketStatusCountsDto,
} from '../types';

export interface RequestsQueryState {
  status: AdminTicketStatus | null;
  category: AdminTicketCategory | null;
  q: string;
  ticket: number | null;
}

export interface ScrollSnapshot {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
}

const Statuses = new Set<AdminTicketStatus>(['new', 'open', 'resolved']);
const Categories = new Set<AdminTicketCategory>(['Bug', 'Question', 'Billing', 'ErrorReport']);
const NearBottomPixels = 48;
const MessageLimit = 20_000;

const readTicket = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const parseRequestsQuery = (search: string): RequestsQueryState => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const status = params.get('status');
  const category = params.get('category');
  const q = params.get('q')?.trim() ?? '';
  return {
    status: Statuses.has(status as AdminTicketStatus) ? status as AdminTicketStatus : null,
    category: Categories.has(category as AdminTicketCategory) ? category as AdminTicketCategory : null,
    q,
    ticket: readTicket(params.get('ticket')),
  };
};

export const serializeRequestsQuery = (state: RequestsQueryState) => {
  const params = new URLSearchParams();
  if (state.status) {
    params.set('status', state.status);
  }
  if (state.category) {
    params.set('category', state.category);
  }
  if (state.q.trim()) {
    params.set('q', state.q.trim());
  }
  if (state.ticket) {
    params.set('ticket', String(state.ticket));
  }

  return params.toString();
};

export const mergeTicketPage = (
  current: AdminTicketListItemDto[],
  incoming: AdminTicketListItemDto[]
) => {
  const merged = [...current];
  const indexById = new Map(merged.map((item, index) => [item.id, index]));
  incoming.forEach((item) => {
    const index = indexById.get(item.id);
    if (index === undefined) {
      indexById.set(item.id, merged.length);
      merged.push(item);
      return;
    }

    if (item.updatedAt >= merged[index].updatedAt) {
      merged[index] = item;
    }
  });

  return merged;
};

export const reconcileTicketStatusCounts = (
  counts: AdminTicketStatusCountsDto,
  previous: AdminTicketStatus,
  canonical: AdminTicketStatus
): AdminTicketStatusCountsDto => {
  if (previous === canonical) {
    return counts;
  }

  return {
    ...counts,
    [previous]: Math.max(0, counts[previous] - 1),
    [canonical]: counts[canonical] + 1,
  };
};

export const validateTicketMessage = (body: string) => {
  const trimmed = body.trim();
  if (!trimmed) {
    return 'Enter a message before sending.';
  }
  if (trimmed.length > MessageLimit) {
    return 'Message must be 20,000 characters or fewer.';
  }

  return null;
};

export const shouldAutoStickToBottom = (snapshot: ScrollSnapshot) =>
  snapshot.scrollHeight - snapshot.scrollTop - snapshot.clientHeight <= NearBottomPixels;

export const buildUserErrorsHref = (traceId: string) =>
  `/super-admin/user-errors?traceId=${encodeURIComponent(traceId)}`;
